import { useEffect, useRef, type RefObject } from "react";

interface UseDismissPopoverArgs<
  T extends HTMLElement,
  P extends HTMLElement,
> {
  open: boolean;
  triggerRef: RefObject<T | null>;
  panelRef: RefObject<P | null>;
  onDismiss: () => void;
}

export function useDismissPopover<
  T extends HTMLElement,
  P extends HTMLElement,
>({ open, triggerRef, panelRef, onDismiss }: UseDismissPopoverArgs<T, P>) {
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (!open) return;
    const isInside = (target: Node) =>
      !!panelRef.current?.contains(target) ||
      !!triggerRef.current?.contains(target);
    const dismiss = () => onDismissRef.current();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss();
    };
    const onPointerDown = (e: PointerEvent) => {
      if (isInside(e.target as Node)) return;
      dismiss();
    };
    const onWheel = (e: WheelEvent) => {
      if (isInside(e.target as Node)) return;
      dismiss();
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("wheel", onWheel, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("wheel", onWheel, true);
    };
  }, [open, panelRef, triggerRef]);
}
